import { useState } from "react";
import { ArrowRight, CheckCircle2, Lock, Phone } from "lucide-react";
import { toast } from "sonner";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await addDoc(collection(db, "contact_messages"), {
        name: form.name.trim(),
        contact: form.contact.trim(),
        message: form.message.trim(),
        createdAt: serverTimestamp(),
      });
      setDone(true);
    } catch {
      toast.error("Couldn't send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mx-auto max-w-screen-xl px-6 py-16 md:px-12 md:py-24" data-testid="contact-page">
      <div className="grid gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-accent">
            <span className="h-px w-10 bg-accent" /> Talk to Eunoia
          </p>
          <h1 className="mt-6 font-serif text-4xl font-bold leading-tight tracking-tight md:text-5xl">
            Not ready to book? Just say hello.
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-muted-foreground md:text-base">
            Questions about therapy, pricing, privacy, or whether this is even for you —
            ask us anything. A real person replies, usually within one working day.
          </p>

          <div className="mt-10 space-y-4">
            <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5">
              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <p className="text-xs leading-relaxed text-muted-foreground">
                Please don't share detailed clinical history here — save that for your therapist,
                where it's fully protected. This form is only for questions and hello's.
              </p>
            </div>
            <div className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5" data-testid="contact-helpline">
              <Phone className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              <p className="text-xs leading-relaxed text-muted-foreground">
                Eunoia is not an emergency service. In crisis right now?{" "}
                <span className="font-semibold text-foreground">KIRAN 1800-599-0019</span> (24×7, free).
                For extreme-case mental health support, please call the national{" "}
                <span className="font-semibold text-foreground">Tele MANAS helpline at 1-800-891-4416</span>.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-7">
          {done ? (
            <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-primary/10 bg-card p-14 text-center" data-testid="contact-success">
              <CheckCircle2 className="h-12 w-12 text-accent" />
              <h2 className="mt-6 font-serif text-3xl font-bold tracking-tight">Message received.</h2>
              <p className="mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
                Thank you for reaching out — that takes a little courage in itself.
                We'll get back to you within one working day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-3xl border border-primary/10 bg-card p-8 md:p-12" data-testid="contact-form">
              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="ct-name" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Name</label>
                  <input
                    id="ct-name" required value={form.name} data-testid="contact-input-name"
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-input bg-background px-5 py-3.5 text-sm outline-none transition-colors focus:border-accent"
                    placeholder="First name is fine"
                  />
                </div>
                <div>
                  <label htmlFor="ct-contact" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Email</label>
                  <input
                    id="ct-contact" type="email" required value={form.contact} data-testid="contact-input-handle"
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                    className="mt-2 w-full rounded-2xl border border-input bg-background px-5 py-3.5 text-sm outline-none transition-colors focus:border-accent"
                    placeholder="Where should we reply?"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="ct-message" className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Your message</label>
                <textarea
                  id="ct-message" required rows={5} value={form.message} data-testid="contact-input-message"
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="mt-2 w-full resize-none rounded-2xl border border-input bg-background px-5 py-3.5 text-sm outline-none transition-colors focus:border-accent"
                  placeholder="Ask us anything — there's no wrong question."
                />
              </div>

              <button
                type="submit" disabled={sending} data-testid="contact-submit"
                className="group mt-8 flex items-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-bold text-primary-foreground transition-all duration-300 enabled:hover:scale-[1.03] enabled:active:scale-95 disabled:opacity-50"
              >
                {sending ? "Sending…" : "Send message"}
                {!sending && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </div>
  );
}
